/**
 * Matterport Viewer Component
 * Embeds Matterport virtual tours via iframe
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../../../store/AppContext';
import '../../../components/viewers/ViewerCommon.css';

export function MatterportViewer() {
  const { state, setStatus } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const project = state.currentProject;
  // Use the first tour from the matterportTours array
  const activeTour = project?.matterportTours?.[0];
  const matterportUrl = activeTour?.url;

  // Convert share URL to embed URL
  const getEmbedUrl = useCallback((url: string): string => {
    try {
      const urlObj = new URL(url);

      // Extract model ID from various URL formats
      let modelId = '';

      // Format: https://my.matterport.com/show/?m=MODEL_ID
      const mParam = urlObj.searchParams.get('m');
      if (mParam) {
        modelId = mParam;
      }

      // Format: https://my.matterport.com/show/MODEL_ID
      if (!modelId && url.includes('/show/')) {
        const match = url.match(/\/show\/([^/?]+)/);
        if (match) {
          modelId = match[1];
        }
      }

      if (modelId) {
        // Return proper embed URL with parameters
        return `https://my.matterport.com/show/?m=${modelId}&play=1&qs=1&hr=0&gt=0&help=0&brand=0&mls=0&dh=1&f=1&fp=1`;
      }

      return url;
    } catch {
      return url;
    }
  }, []);

  // Create and manage iframe
  useEffect(() => {
    if (!matterportUrl) {
      // Clean up existing iframe
      if (iframeRef.current && containerRef.current) {
        try {
          if (containerRef.current.contains(iframeRef.current)) {
            containerRef.current.removeChild(iframeRef.current);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        iframeRef.current = null;
      }
      return;
    }

    if (!containerRef.current) return;

    setIsLoading(true);
    setError(null);

    const embedUrl = getEmbedUrl(matterportUrl);

    // Create iframe element
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', embedUrl);
    iframe.setAttribute('allow', 'fullscreen; xr-spatial-tracking');
    iframe.style.cssText = 'width:100%;height:100%;border:none;';

    // Store reference
    iframeRef.current = iframe;

    // Handle load events
    const handleLoad = () => {
      setIsLoading(false);
      setStatus('Matterport tour loaded');
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load Matterport tour');
      setStatus('Failed to load Matterport tour');
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    containerRef.current.appendChild(iframe);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);

      // Clean up iframe on unmount
      if (iframeRef.current && containerRef.current) {
        try {
          if (containerRef.current.contains(iframeRef.current)) {
            containerRef.current.removeChild(iframeRef.current);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        iframeRef.current = null;
      }
    };
  }, [matterportUrl, getEmbedUrl, setStatus]);

  // Show empty state
  if (!matterportUrl) {
    return (
      <div className="viewer">
        <div className="viewer__setup">
          <div className="viewer__setup-content">
            <svg
              className="viewer__setup-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>

            <h3 className="viewer__setup-title">No Virtual Tour</h3>

            <p className="viewer__setup-description">
              No virtual tour available for this project
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer">
      <div className="viewer__content">
        {/* Iframe container */}
        <div
          ref={containerRef}
          className="viewer__webview-container"
          style={{ width: '100%', height: '100%' }}
        />

        {isLoading && (
          <div className="viewer__loading">
            <div className="viewer__loading-spinner" />
            <span className="viewer__loading-text">Loading Matterport tour...</span>
          </div>
        )}
        {error && (
          <div className="viewer__error">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
